import React from "react";

const InputField = React.forwardRef(
  (
    {
      label,
      type,
      placeholder,
      name,
      required = false,
      className,
      startIcon,
      endIcon,
      outerClassName,
      value,
      fixedDigit,
      divClassName,
      // ref,
      colorize = false, // New prop to enable value-based coloring
      error = false,
      helperText,
      // Opt-in textarea, for values with no sensible length limit. Everything around the control
      // (label, border, error state, helper text) stays identical so the two read as one field.
      multiline = false,
      rows = 3,
      onChange,
      onInput,
      ...props
    },
    ref
  ) => {
    // Determine if value is a number and its sign
    const parsedValue = parseFloat(value);
    const isNumber = !isNaN(parsedValue);
    const isNegative = isNumber && parsedValue < 0;
    const isPositive = isNumber && parsedValue > 0;

    // Conditional classes for text and border color
    const textColorClass = colorize
      ? isNegative
        ? "text-red-500"
        : isPositive
        ? "text-green-500"
        : ""
      : "";
    const borderColorClass = colorize
      ? isNegative
        ? "border-red-500"
        : isPositive
        ? "border-green-500"
        : "border-app-border"
      : "border-app-border";

    const clampDateYear = (raw) => {
      if (type !== "date") return raw;
      if (raw == null) return raw;
      const str = String(raw);
      // Expected shape: YYYY-MM-DD (but allow partial typing)
      const parts = str.split("-");
      if (!parts.length) return str;
      const year = parts[0] ?? "";
      if (year.length <= 4) return str;
      parts[0] = year.slice(0, 4);
      return parts.join("-");
    };

    const handleChange = (e) => {
      if (type === "date" && e?.target) {
        const next = clampDateYear(e.target.value);
        if (next !== e.target.value) {
          e.target.value = next;
        }
      }
      onChange?.(e);
    };

    const handleInput = (e) => {
      if (type === "date" && e?.target) {
        const next = clampDateYear(e.target.value);
        if (next !== e.target.value) {
          e.target.value = next;
        }
      }
      onInput?.(e);
    };

    return (
      <div className={`w-full ${divClassName}`}>
        {label && (
          <label
            className="block uppercase text-app-text-secondary text-xs font-semibold mb-2"
            htmlFor={label}
          >
            {label || "label"} {required && <sup className="text-red-500">*</sup>}
          </label>
        )}
        <div
          className={`flex ${
            multiline ? "items-start" : "items-center"
          } gap-1 border rounded w-full transition-all duration-150 ${
            props.disabled
              ? "bg-app-surface-muted cursor-not-allowed opacity-70"
              : "bg-app-surface text-app-text focus-within:ring focus-within:ring-primary/30"
          } ${error ? "border-red-500" : "border-app-border"} ${outerClassName}`}
        >
          {startIcon && <div className="pl-2 text-app-text-muted">{startIcon}</div>}
          {multiline ? (
            <textarea
              className={`placeholder:text-app-text-muted bg-transparent text-sm p-2 rounded w-full text-app-text resize-y ${
                props.disabled
                  ? "cursor-not-allowed bg-app-surface-muted text-app-text placeholder-gray-400"
                  : "focus:outline-none focus:ring-0"
              } ${className}`}
              placeholder={placeholder || label}
              name={name}
              required={required}
              rows={rows}
              value={value ?? ""}
              onChange={handleChange}
              onInput={handleInput}
              ref={ref}
              {...props}
            />
          ) : (
            <input
              type={type || "text"}
              className={`placeholder:text-app-text-muted bg-transparent text-sm p-2 rounded w-full text-app-text ${
                props.disabled
                  ? "cursor-not-allowed bg-app-surface-muted text-app-text placeholder-gray-400"
                  : "focus:outline-none focus:ring-0"
              } ${textColorClass} ${className}`}
              placeholder={placeholder || label}
              name={name}
              required={required}
              value={
                type === "number" && fixedDigit ? Number(value)?.toFixed(fixedDigit) : value ?? ""
              }
              onChange={handleChange}
              onInput={handleInput}
              onFocus={(event) => !props.disabled && event.target.select()}
              onWheel={
                type === "number"
                  ? (e) => {
                      // e.preventDefault();
                      e.target.blur();
                    }
                  : undefined
              }
              ref={ref}
              {...props}
            />
          )}
          {endIcon && <div className="pr-2 text-app-text-muted">{endIcon}</div>}
        </div>
        {helperText && (
          <p className={`text-xs mt-1 ${error ? "text-red-500" : "text-app-text-muted"}`}>
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

export default InputField;
